import React, { useEffect, useState } from "react";

import { formatDateLocalized } from "shared/utils/formatDateLocalized";
import { DatePickerMobile } from "../../DatePicker/Mobile";

import { DatePickerActions } from "Components/atoms/DatePickerActions/Mobile";
import { Icon } from "Components/atoms/Icon";
import { getFormattedDateDetails } from "shared/utils/getFormattedDateDetails";
import { DatePickerWithInputProps } from "../types";
import {
  DatePickerActionsMobile,
  HoverIcon,
  InputContainerMobile,
  PlaceholderMobile,
  SelectedDate,
  SelectedDateMobile,
  SelectedDatesContainerMobile,
  WrapperInputMobile,
  WrapperMobile,
  WrapperSelectedDatesMobile,
  WrapperTitleMobile,
} from "./styles";

/**
 * Componente DatePickerWithInputMobile
 *
 * Combina um campo de entrada com um DatePicker, permitindo seleção múltipla de datas.
 *
 * @param {Array} daysInMonth - Lista de dias do mês com estados de seleção.
 * @param {number} maxSelectableDates - Número máximo de datas selecionáveis.
 * @param {string} placeholder - Texto exibido como placeholder no campo de entrada.
 * @param {string} title - Título exibido no DatePicker.
 * @param {(dates: string[]) => void} onDatesChange - Callback executado ao alterar as datas selecionadas.
 * @param {string[]} [selectedDates=[]] - Lista de datas previamente selecionadas.
 * @param {'origin' | 'destination'} [type='origin'] - Define o tipo do campo.
 * @param {Boolean} isDisabled  - Indica se está desabilitado
 */
export const DatePickerWithInputMobile: React.FC<DatePickerWithInputProps> = ({
  title,
  type,
  icon,
  placeholder,
  daysInMonth,
  maxSelectableDates,
  isVisible,
  selectedDates = [],
  onDatesChange = () => {},
  setIsVisible = () => {},
  onConfirmAction,
  onClick,
  isDisabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isVisible !== undefined) setIsOpen(isVisible);
  }, [isVisible]);

  const handleRemoveDate = (dateToRemove: string) => {
    const updatedDates = selectedDates.filter((date) => date !== dateToRemove);
    onDatesChange(updatedDates);
  };

  const actionClear = () => {
    onDatesChange([]);
  };

  const actionConfirm = () => {
    onConfirmAction && onConfirmAction();
  };

  return (
    <WrapperMobile>
      <InputContainerMobile
        onClick={() => !isDisabled && onClick && onClick()}
        aria-label="Select dates"
        isOpen={isOpen}
      >
        <WrapperInputMobile
          isVisible={isOpen}
          selectedDates={selectedDates.length === 0}
        >
          {icon}
          {selectedDates.length === 0 || isOpen ? (
            <PlaceholderMobile isOpen={isOpen}>{placeholder}</PlaceholderMobile>
          ) : (
            <WrapperSelectedDatesMobile>
              {selectedDates.map((date) => (
                <SelectedDate
                  type={type || "origin"}
                  key={date}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveDate(date);
                  }}
                  aria-label={`Remove data ${formatDateLocalized(date)}`}
                >
                  <span>{getFormattedDateDetails(date).day}</span>
                  <span>{getFormattedDateDetails(date).monthShort}</span>

                  {type === "destination" ? (
                    <Icon
                      library="go"
                      name="GoArrowDownLeft"
                      size="extraSmall"
                      color="black"
                    />
                  ) : (
                    <Icon
                      library="go"
                      name="GoArrowUpRight"
                      size="extraSmall"
                      color="black"
                    />
                  )}
                  <HoverIcon>
                    <Icon
                      library="md"
                      name="MdOutlineClose"
                      size="small"
                      color="black"
                    />
                  </HoverIcon>
                </SelectedDate>
              ))}
            </WrapperSelectedDatesMobile>
          )}
        </WrapperInputMobile>
        <WrapperTitleMobile isOpen={isOpen}>{title}</WrapperTitleMobile>
        {isOpen && (
          <DatePickerActionsMobile>
            <SelectedDatesContainerMobile>
              {selectedDates.map((date) => (
                <SelectedDateMobile
                  type={type || "origin"}
                  key={date}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveDate(date);
                  }}
                  aria-label={`Remove date ${formatDateLocalized(date)}`}
                >
                  <span>{getFormattedDateDetails(date).day}</span>/
                  <span>{getFormattedDateDetails(date).month}</span>
                </SelectedDateMobile>
              ))}
            </SelectedDatesContainerMobile>
            <DatePickerActions
              onClear={actionClear}
              onConfirm={actionConfirm}
            />
          </DatePickerActionsMobile>
        )}
      </InputContainerMobile>

      <DatePickerMobile
        maxSelectableDates={maxSelectableDates}
        daysInMonth={daysInMonth}
        selectedDates={selectedDates}
        onDatesChange={onDatesChange}
        onConfirm={actionConfirm}
        onClear={actionClear}
        closePicker={() => {
          setIsVisible(false);
          onConfirmAction && onConfirmAction();
        }}
        type={type}
      />
    </WrapperMobile>
  );
};
